import { createContext, useContext, useState, useCallback, useEffect } from "react";

/**
 * Available tabs in the Social Drawer
 * Phase 1: Activity + Friends
 * Future: Chat, Clips, Earn
 */
export type SocialDrawerTab = "activity" | "feed" | "friends" | "chat" | "clips" | "earn" | "connections" | "stats";

interface ChatTarget {
    /** Platform userId of the conversation partner */
    userId: string;
    /** Display name shown in the chat thread header */
    username: string;
    /** Optional avatar URL for the chat thread header */
    avatarUrl?: string;
}

interface SocialDrawerState {
    isOpen: boolean;
    isDocked: boolean;
    activeTab: SocialDrawerTab;
    chatTarget: ChatTarget | null;
}

interface SocialDrawerContextValue extends SocialDrawerState {
    openDrawer: (tab?: SocialDrawerTab) => void;
    closeDrawer: () => void;
    setActiveTab: (tab: SocialDrawerTab) => void;
    toggleDrawer: () => void;
    toggleDocked: () => void;
    openChatWith: (userId: string, username: string, avatarUrl?: string) => void;
    clearChatTarget: () => void;
}

const STORAGE_KEY = "socialDrawer_lastTab";
const DOCKED_STORAGE_KEY = "socialDrawer_docked";

/*
 * Safari private mode (and any locked-down storage policy) leaves
 * `window` defined but makes `localStorage` null OR throws on access —
 * the `typeof window` guard alone wasn't enough and crashed the drawer
 * on mount (e.g. /discord-callback). Read/write through these so a
 * blocked store degrades to in-memory defaults instead of throwing.
 */
function readStored(key: string): string | null {
    try {
        return localStorage.getItem(key);
    } catch {
        return null;
    }
}

function writeStored(key: string, value: string): void {
    try {
        localStorage.setItem(key, value);
    } catch {
        // storage unavailable — preference simply doesn't persist
    }
}

/**
 * Default context value - drawer closed, activity tab, not docked
 */
const defaultValue: SocialDrawerContextValue = {
    isOpen: false,
    isDocked: false,
    activeTab: "activity",
    chatTarget: null,
    openDrawer: () => {},
    closeDrawer: () => {},
    setActiveTab: () => {},
    toggleDrawer: () => {},
    toggleDocked: () => {},
    openChatWith: () => {},
    clearChatTarget: () => {},
};

export const SocialDrawerContext = createContext<SocialDrawerContextValue>(defaultValue);

/**
 * Hook to access Social Drawer state and actions.
 *
 * Usage:
 *   const { isOpen, isDocked, activeTab, openDrawer, closeDrawer, toggleDocked } = useSocialDrawer();
 *
 *   // Open to specific tab
 *   openDrawer("friends");
 *
 *   // Navigate directly to a DM thread (e.g. from Friend Profile → Message button)
 *   openChatWith(userId, username, avatarUrl);
 *
 *   // Toggle docked column mode (desktop only — CSS hides the effect on mobile)
 *   toggleDocked();
 */
export function useSocialDrawer(): SocialDrawerContextValue {
    return useContext(SocialDrawerContext);
}

/**
 * Hook for the SocialDrawerProvider to manage state.
 * Consumed exclusively by SocialDrawerProvider — do not call directly in components.
 * Use useSocialDrawer() instead.
 */
export function useSocialDrawerState(): SocialDrawerContextValue {
    const [isOpen, setIsOpen] = useState(false);
    const [chatTarget, setChatTarget] = useState<ChatTarget | null>(null);
    const [isDocked, setIsDocked] = useState(() => {
        return readStored(DOCKED_STORAGE_KEY) === "true";
    });
    const [activeTab, setActiveTabState] = useState<SocialDrawerTab>(() => {
        const saved = readStored(STORAGE_KEY);
        if (saved && ["activity", "feed", "friends", "chat", "clips", "earn", "connections", "stats"].includes(saved)) {
            return saved as SocialDrawerTab;
        }
        return "activity";
    });

    // Persist tab preference
    useEffect(() => {
        writeStored(STORAGE_KEY, activeTab);
    }, [activeTab]);

    // Persist docked preference + apply/remove body class for CSS page shift
    useEffect(() => {
        writeStored(DOCKED_STORAGE_KEY, String(isDocked));
        if (isDocked) {
            document.body.classList.add("social-docked");
        } else {
            document.body.classList.remove("social-docked");
        }
        return () => {
            document.body.classList.remove("social-docked");
        };
    }, [isDocked]);

    // Prevent body scroll when drawer is open — only when NOT docked (docked panel is always visible)
    useEffect(() => {
        if (isOpen && !isDocked) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
        }

        return () => {
            document.body.style.overflow = "";
        };
    }, [isOpen, isDocked]);

    // ESC key closes drawer — only when not docked
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape" && isOpen && !isDocked) {
                setIsOpen(false);
            }
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [isOpen, isDocked]);

    const openDrawer = useCallback((tab?: SocialDrawerTab) => {
        if (tab) {
            setActiveTabState(tab);
        }
        setIsOpen(true);
    }, []);

    const closeDrawer = useCallback(() => {
        setIsOpen(false);
    }, []);

    const setActiveTab = useCallback((tab: SocialDrawerTab) => {
        setActiveTabState(tab);
    }, []);

    const toggleDrawer = useCallback(() => {
        setIsOpen((prev) => !prev);
    }, []);

    const toggleDocked = useCallback(() => {
        setIsDocked((prev) => !prev);
    }, []);

    const openChatWith = useCallback((userId: string, username: string, avatarUrl?: string) => {
        setChatTarget({ userId, username, avatarUrl });
    }, []);

    const clearChatTarget = useCallback(() => {
        setChatTarget(null);
    }, []);

    return {
        isOpen,
        isDocked,
        activeTab,
        chatTarget,
        openDrawer,
        closeDrawer,
        setActiveTab,
        toggleDrawer,
        toggleDocked,
        openChatWith,
        clearChatTarget,
    };
}

export default useSocialDrawer;
